import {z} from 'zod';

import {
  BaseVoidResponseSchema,
  CreatedAtAndUpdatedBySchema,
  CreatedAndUpdatedBySchema,
  HeaderDynamicActionsFlagColoursSchema,
  PaymentTypeSchema,
} from '@dms/api/shared';

/**
 * Base
 */
const ReceiveNoteStateSchema = z.enum(['PENDING', 'COMPLETED']);
export type ReceiveNoteState = z.infer<typeof ReceiveNoteStateSchema>;

const DeliveryNoteStateSchema = z.enum(['PENDING', 'RECEIVED', 'COMPLETED']);
export type DeliveryNoteState = z.infer<typeof DeliveryNoteStateSchema>;

/**
 * GET
 */
export const GetReceiveNoteRequestSchema = z.object({
  receiveNoteId: z.string(),
});
export type GetReceiveNoteRequest = z.infer<typeof GetReceiveNoteRequestSchema>;

const MarkerSchema = z.object({
  title: z.string(),
  color: HeaderDynamicActionsFlagColoursSchema,
  key: z.string(),
});
export type Marker = z.infer<typeof MarkerSchema>;

const DeliveryNoteDetailsSchema = z.object({
  deliveryNoteId: z.string(),
  deliveryNoteNumber: z.string(),
  deliveryNoteIssuedAt: z.string(),
  createdAt: z.string(),
  completedAt: z.string().nullish(),
  isFromReceiveNote: z.boolean(),
  note: z.string().nullish(),
});
export type DeliveryNoteDetails = z.infer<typeof DeliveryNoteDetailsSchema>;

export const GetReceiveNoteResponseSchema = z
  .object({
    receiveNoteId: z.string(),
    receiveNoteNumber: z.string().nullish(),
    state: ReceiveNoteStateSchema,
    type: z.enum(['EXTERNAL_TRANSFER', 'OPENING_BALANCE', 'PURCHASE', 'TRANSFER']),
    markers: z.array(MarkerSchema),
    completedAt: z.string().nullish(),
    completedBy: z.string().nullish(),
    supplierId: z.string(),
    warehouseId: z.string(),
    authorizationProfileId: z.string(),
    transferSourceBranchId: z.string().optional(),
    branchId: z.string(),
    sourceBranchId: z.string().nullish(),
    invoiceNumber: z.string().nullish(),
    invoiceIssueDate: z.string().optional(),
    paymentType: PaymentTypeSchema,
    currency: z.string(),
    exchangeRate: z.number().nullish(),
    ncConversion: z.string().nullish(),
    isFastTrackTabEnable: z.boolean(),
    note: z.string().nullish(),
    deliveryNote: DeliveryNoteDetailsSchema,
  })
  .merge(CreatedAtAndUpdatedBySchema);
export type GetReceiveNoteResponse = z.infer<typeof GetReceiveNoteResponseSchema>;

export const GetReceiveNoteFastTrackRequestSchema = z.object({
  receiveNoteId: z.string(),
});
export type GetReceiveNoteFastTrackRequest = z.infer<typeof GetReceiveNoteFastTrackRequestSchema>;

export const FastTruckIssueSchema = z.object({
  isSelected: z.boolean(),
  isSelectionAllowed: z.boolean(),
  fastTruckId: z.string(),
  fastTruckItemId: z.string(),
  fastTruckNumber: z.string(),
  fastTruckDate: z.date(),
  fastTruckQuantity: z.number(),
  fastTrackNote: z.string(),
  customerOrderNumber: z.string(),
  customerOrderCreatedAt: z.date(),
  customerOrderVehicleId: z.string(),
  customerOrderCustomerId: z.string(),
  fastTrackOriginEntityHeaderId: z.string(),
  fastTrackOriginEntityHeaderType: z.string(),
  fastTrackOriginEntityId: z.string(),
  fastTrackOriginEntityType: z.string(),
  fastTrackOriginEntityHeaderServiceCaseId: z.string(),
});
export type FastTruckIssue = z.infer<typeof FastTruckIssueSchema>;

export const ReceiveNoteItemSchema = z.object({
  receiveNoteItemId: z.string(),
  catalogNumber: z.string(),
  manufacturerId: z.string(),
  name: z.string(),
  quantity: z.number(),
  unitId: z.string(),
  quantityLeft: z.number(),
  isAccepted: z.boolean(),
  fastTruckIssues: z.array(FastTruckIssueSchema),
});
export type ReceiveNoteItem = z.infer<typeof ReceiveNoteItemSchema>;

export const GetReceiveNoteFastTrackResponseSchema = z.object({
  receiveNoteItems: z.array(ReceiveNoteItemSchema),
});
export type GetReceiveNoteFastTrackResponse = z.infer<typeof GetReceiveNoteFastTrackResponseSchema>;

/**
 * POST
 */
export const PostReceiveNoteManualRequestSchema = z.object({
  warehouseId: z.string(),
  invoiceNumber: z.string().nullish(),
  deliveryNoteNumber: z.string().nullish(),
  supplierId: z.string(),
  paymentType: PaymentTypeSchema,
  orderNumber: z.string().nullish(),
  exchangeRate: z.number().nullish(),
  ncConversion: z.string().nullish(),
  note: z.string().nullish(),
  authorizationProfileId: z.string(),
  invoiceIssueDate: z.string().nullish(),
  delivery: z
    .object({
      deliveryNoteId: z.string(),
    })
    .nullish(),
});
export type PostReceiveNoteManualRequest = z.infer<typeof PostReceiveNoteManualRequestSchema>;

export const PostReceiveNoteManualResponseSchema = z.object({
  receiveNoteId: z.string(),
  receiveNoteItemId: z.string(),
});
export type PostReceiveNoteManualResponse = z.infer<typeof PostReceiveNoteManualResponseSchema>;

/**
 * PATCH
 */
export const PatchReceiveNoteManualRequestSchema = z.object({
  receiveNoteId: z.string(),
  body: z.object({
    deliveryNoteNumber: z.string().nullish(),
    warehouseId: z.string(),
    supplierId: z.string().nullish(),
    transferSourceBranchId: z.string().optional(),
    invoiceNumber: z.string().nullish(),
    paymentType: PaymentTypeSchema,
    exchangeRate: z.number().nullish(),
    ncConversion: z.string().nullish(),
    note: z.string().nullish(),
    authorizationProfileId: z.string(),
    invoiceIssueDate: z.string().nullish(),
    receiveNoteNumber: z.string().nullish(),
    issuedAt: z.string().nullish(),
  }),
});
export type PatchReceiveNoteManualRequest = z.infer<typeof PatchReceiveNoteManualRequestSchema>;

export const PatchReceiveNoteManualResponseSchema = z.object({
  deliveryNote: z
    .object({
      deliveryNoteId: z.string(),
      supplierId: z.string(),
      warehouseId: z.string(),
      authorizationProfileId: z.string(),
      state: DeliveryNoteStateSchema,
      deliveryNoteNumber: z.string(),
      invoiceNumber: z.string(),
      paymentType: PaymentTypeSchema,
      currency: z.string(),
      exchangeRate: z.number(),
      referenceNumber: z.string(),
      ncConversion: z.string(),
      issuedAt: z.string(),
      isFromReceiveNote: z.boolean(),
      invoiceIssueDate: z.string().optional(),
    })
    .merge(CreatedAndUpdatedBySchema),
  receiveNote: z
    .object({
      receiveNoteId: z.string(),
      warehouseId: z.string(),
      deliveryNoteId: z.string(),
      supplierId: z.string(),
      receiveNoteNumber: z.string(),
      state: ReceiveNoteStateSchema,
      paymentType: PaymentTypeSchema,
      currency: z.string(),
      exchangeRate: z.number(),
      ncConversion: z.string(),
      note: z.string(),
      authorizationProfileId: z.string(),
    })
    .merge(CreatedAndUpdatedBySchema),
});
export type PatchReceiveNoteManualResponse = z.infer<typeof PatchReceiveNoteManualResponseSchema>;

export const PatchReceiveNoteStateRequestSchema = z.object({
  receiveNoteId: z.string(),
  body: z.object({
    state: ReceiveNoteStateSchema,
  }),
});
export type PatchReceiveNoteStateRequest = z.infer<typeof PatchReceiveNoteStateRequestSchema>;

export const PatchReceiveNoteStateResponseSchema = BaseVoidResponseSchema;
export type PatchReceiveNoteStateResponse = z.infer<typeof PatchReceiveNoteStateResponseSchema>;

export const PatchReceiveNoteCompletedRequestSchema = z.object({
  receiveNoteId: z.string(),
  body: z.object({
    deliveryNoteNumber: z.string().nullish(),
    invoiceNumber: z.string().nullish(),
    note: z.string().nullish(),
    invoiceIssueDate: z.string().nullish(),
    issuedAt: z.string().nullish(),
  }),
});
export type PatchReceiveNoteCompletedRequest = z.infer<
  typeof PatchReceiveNoteCompletedRequestSchema
>;

export const PatchReceiveNoteCompletedResponseSchema = BaseVoidResponseSchema;
export type PatchReceiveNoteCompletedResponse = z.infer<
  typeof PatchReceiveNoteCompletedResponseSchema
>;

export const FastTrackItemSchema = z.object({
  fastTrackItemId: z.string(),
  isSelected: z.boolean(),
});

export type FastTrackItem = z.infer<typeof FastTrackItemSchema>;

export const PatchReceiveNoteItemSchema = z.object({
  receiveNoteItemId: z.string(),
  isAccepted: z.boolean(),
  fastTrackItem: z.array(FastTrackItemSchema),
});
export type PatchReceiveNoteItem = z.infer<typeof PatchReceiveNoteItemSchema>;

export const PatchReceiveNoteRequestSchema = z.object({
  receiveNoteId: z.string(),
  body: z.object({
    receiveNoteItem: z.array(PatchReceiveNoteItemSchema),
  }),
});
export type PatchReceiveNoteRequest = z.infer<typeof PatchReceiveNoteRequestSchema>;

export const PatchReceiveNoteResponseSchema = z.void();
export type PatchReceiveNoteResponse = z.infer<typeof PatchReceiveNoteResponseSchema>;

export const PatchReceiveNoteBulkEditRequestSchema = z.object({
  body: z.object({
    receiveNoteIds: z.array(z.string()),
    invoiceNumber: z.string().nullish(),
    invoiceIssueDate: z.string().nullish(),
    deliveryNoteNumber: z.string().nullish(),
    deliveryNoteIssueDate: z.string().nullish(),
  }),
});
export type PatchReceiveNoteBulkEditRequest = z.infer<typeof PatchReceiveNoteBulkEditRequestSchema>;

export const PatchReceiveNoteBulkEditResponseSchema = BaseVoidResponseSchema;
export type PatchReceiveNoteBulkEditResponse = z.infer<
  typeof PatchReceiveNoteBulkEditResponseSchema
>;

/**
 * DELETE
 */
export const BulkDeleteReceiveNoteRequestSchema = z.object({
  receiveNoteId: z.array(z.string()),
});
export type BulkDeleteReceiveNoteRequest = z.infer<typeof BulkDeleteReceiveNoteRequestSchema>;

export const BulkDeleteReceiveNoteResponseSchema = BaseVoidResponseSchema;
export type BulkDeleteReceiveNoteResponse = z.infer<typeof BulkDeleteReceiveNoteResponseSchema>;
